export type ApplyCouponPayload = {
  promoCode: string
  cartProducts: Array<{ sku: string }>
}

export type ApplyCouponResponse = {
  valid: boolean
  data?: any
  error?: string
  disqualifyingSkuError?: string
}

export async function applyCoupon({
  promoCode,
  cartProducts,
}: ApplyCouponPayload): Promise<ApplyCouponResponse> {
  const res = await fetch(import.meta.env.VITE_WEBHOOKS_URL + '/coupons', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      promoCode,
      products: cartProducts,
    }),
  })

  const json = await res.json()
  const data = json?.data ?? {}

  let isValid = data?.valid ?? false

  // Check if promo applies to any products in the cart
  const validProducts = data?.appliesTo ?? []
  if (isValid && validProducts.length) {
    isValid = cartProducts.some((p) => validProducts.includes(p.sku))
  }

  // Check for disqualifying SKUs
  let disqualifyingSkuError = ''
  const disqualifyingSkus = data?.disqualifyingSkus ?? []
  if (isValid && disqualifyingSkus.length) {
    for (const product of cartProducts) {
      if (disqualifyingSkus.includes(product.sku)) {
        isValid = false
        disqualifyingSkuError =
          'Promo code could not be applied due to disqualifying items in the cart.'
        break
      }
    }
  }

  // Check training requirement
  const requiredAnyTraining = data?.metadata?.requiredAnyTraining
  if (isValid && requiredAnyTraining) {
    const trainingSkus = requiredAnyTraining.split(',')
    isValid = cartProducts.some((p) => trainingSkus.includes(p.sku))
    if (!isValid) {
      disqualifyingSkuError =
        'Promo code could not be applied due to missing or incorrect training item.'
    }
  }

  // Check subscription requirement
  const requiredAnySubscription = data?.metadata?.requiredAnySubscription
  if (isValid && requiredAnySubscription) {
    const subscriptionSkus = requiredAnySubscription.split(',')
    isValid = cartProducts.some((p) => subscriptionSkus.includes(p.sku))
    if (!isValid) {
      disqualifyingSkuError =
        'Promo code could not be applied due to missing or incorrect subscription.'
    }
  }

  return {
    valid: isValid,
    data: isValid ? data : undefined,
    error: isValid ? undefined : disqualifyingSkuError || 'Promo code is not valid.',
    disqualifyingSkuError: isValid ? undefined : disqualifyingSkuError,
  }
}
